import React, { useState, useEffect, useRef } from 'react';
import * as Lucide from 'lucide-react';
import './MainNavigation.css';

// Sidebar navigation structure
const navigationStructure = [
    {
        id: 'support-area',
        name: 'Support',
        icon: '🛟',
        color: '#00ffc6',
        items: [
            { id: 'dashboard', name: 'Dashboard', icon: '📊' },
            { id: 'self-info', name: 'Self Info', icon: '👤' },
            { id: 'team', name: 'Team Liste', icon: '👥' },
            { id: 'support', name: 'Support Fälle', icon: '🎫' },
            { id: 'support-guide', name: 'Leitfaden', icon: '📖' },
            { id: 'ticket-guide', name: 'Ticket Leitfaden', icon: '🎫' },
            { id: 'penalties', name: 'Strafkatalog', icon: '⚖️' },
            { id: 'team-rules', name: 'Teamregeln', icon: '📜' },
            { id: 'announcements', name: 'Ankündigungen', icon: '📢' },
            { id: 'einreise', name: 'Einreise', icon: '🛬' }
        ]
    },
    {
        id: 'analyse',
        name: 'Analyse',
        icon: '🔍',
        color: '#007bff',
        items: [
            { id: 'analysieren', name: 'User Analyse', icon: '📝', requiredPermission: 'view_analysis' },
        ]
    },
    {
        id: 'faction-management',
        name: 'Fraktionen',
        icon: '⚔️',
        color: '#9b59b6',
        items: [
            { id: 'faction-overview', name: 'Fraktion', icon: '📋', requiredPermission: 'view_faction_overview' },
        ]
    },
    {
        id: 'team-management',
        name: 'Team',
        icon: '📊',
        color: '#f59f00',
        requiredPermissions: ['view_team_management', 'manage_meetings', 'team_view'],
        items: [
            { id: 'team-meetings', name: 'Besprechungen', icon: '🗣️' },
            { id: 'team-actions', name: 'Verwaltung', icon: '⚖️', requiredPermission: 'manage_team_members' },
            { id: 'team-notes', name: 'Notizen', icon: '📝', requiredPermission: 'view_whitelist_stats' },
            { id: 'team-stats', name: 'Statistiken', icon: '📊', requiredPermission: 'view_whitelist_stats' },
            { id: 'absences', name: 'Abwesenheiten', icon: '🏖️', requiredPermission: 'view_whitelist_stats' }
        ]
    },
{
    id: 'verwaltung',
    name: 'Verwaltung',
    icon: '🏢',
    color: '#8B4513',
    items: [
        { id: 'streaming', name: 'Streaming', icon: '📺', requiredPermission: 'manage_streamers' }
    ]
},
    {
        id: 'admin',
        name: 'Admin',
        icon: '⚡',
        color: '#ff6b6b',
        requiredPermissions: ['admin_access', 'view_admin_panel'],
        items: [
            { id: 'admin', name: 'Admin Panel', icon: '🔒' },
            { id: 'commands', name: 'Commands', icon: '💻', requiredPermission: 'manage_commands' },
            { id: 'backups', name: 'Ankündigung', icon: '📢' }
        ]
    },
    {
        id: 'zusatz-module',
        name: 'Zusatz Module',
        icon: '🧩',
        color: '#ff6b6b',
        requiredPermissions: ['view_extra_modules'],
        items: [
            { id: 'giveaway', name: 'Giveaway', icon: '🎁', requiredPermission: 'manage_giveaways' },
            { id: 'umfragen', name: 'Umfragen', icon: '📊', requiredPermission: 'manage_polls' },
            { id: 'automod', name: 'Automod', icon: '🛡️', requiredPermission: 'manage_automod' },
            { id: 'user-check', name: 'User Check', icon: '🔎', requiredPermission: 'view_user_check' }
        ]
    },
];

// Find category ID that contains the currentView
const getCategoryOfView = (viewId) => {
    return navigationStructure.find(cat => 
        cat.items.some(item => item.id === viewId)
    )?.id || 'support-area';
};

const MainNavigation = ({
    currentView,
    setCurrentView,
    isAdmin,
    userData,
    handleLogout,
    hasPermission
}) => {
    const checkIsAdmin = () => {
        if (userData?.id === process.env.REACT_APP_SUPER_ADMIN_USER) return true;
        return typeof isAdmin === 'function' ? isAdmin() : isAdmin;
    };

    const isGlobalAdmin = checkIsAdmin();
    const navRef = useRef(null);

    const canShowItem = (item) => {
        if (isGlobalAdmin) return true;
        if (!item.requiredPermission) return true;
        return hasPermission(item.requiredPermission);
    };

    const canShowCategory = (category) => {
        if (isGlobalAdmin) return true;
        if (category.requiredPermissions) {
            return category.requiredPermissions.some(perm => hasPermission(perm));
        }
        return category.items.some(item => canShowItem(item));
    };

    // State for expanded categories (multiple can be open)
    const [expandedCategories, setExpandedCategories] = useState({
        [getCategoryOfView(currentView)]: true
    });

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Keep active category expanded on view changes
    useEffect(() => {
        const activeCat = getCategoryOfView(currentView);
        setExpandedCategories(prev => ({
            ...prev,
            [activeCat]: true
        }));
    }, [currentView]);

    // Close mobile menu on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mobileMenuOpen && navRef.current && !navRef.current.contains(event.target)) {
                setMobileMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [mobileMenuOpen]);

    const toggleCategory = (catId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [catId]: !prev[catId]
        }));
    };

    const handleItemClick = (itemId) => {
        setCurrentView(itemId);
        setMobileMenuOpen(false);
    };

    // Lucide Icon mapper
    const resolveIcon = (name) => {
        if (typeof Lucide[name] === 'function') return Lucide[name];
        if (process.env.NODE_ENV !== 'production') {
            console.error(`[MainNavigation] Lucide icon "${name}" does not exist. Falling back to Zap.`);
        }
        return Lucide.Zap;
    };

    const getIcon = (id, fallbackEmoji) => {
        const mapping = {
            // Categories
            'support-area': 'LifeBuoy',
            'analyse': 'Search',
            'faction-management': 'Swords',
            'team-management': 'Users',
            'verwaltung': 'Building2',
            'admin': 'ShieldAlert',

            // Views
            'dashboard': 'LayoutDashboard',
            'self-info': 'UserSquare2',
            'team': 'Users2',
            'support': 'Ticket',
            'support-guide': 'BookOpenCheck',
            'ticket-guide': 'FileText',
            'penalties': 'Scale',
            'team-rules': 'ScrollText',
            'announcements': 'Megaphone',
            'einreise': 'PlaneLanding',
            'analysieren': 'LineChart',
            'faction-overview': 'ClipboardList',
            'team-meetings': 'MessageSquareCode',
            'team-actions': 'ShieldAlert',
            'team-notes': 'FileSpreadsheet',
            'team-stats': 'BarChart3',
            'absences': 'CalendarDays',
            'streaming': 'Tv',
            'admin-panel': 'Lock',
            'commands': 'Terminal',
            'backups': 'Megaphone',
            'user-check': 'SearchCheck',
        };

        const iconName = mapping[id];
        if (iconName) {
            const IconComponent = resolveIcon(iconName);
            return <IconComponent className="nav-icon" size={18} />;
        }
        return <span className="nav-icon-emoji">{fallbackEmoji || '⚡'}</span>;
    };

    const IconX = resolveIcon('X');
    const IconMenu = resolveIcon('Menu');
    const IconChevronDown = resolveIcon('ChevronDown');
    const IconSearch = resolveIcon('Search');
    const IconBell = resolveIcon('Bell');
    const IconMoon = resolveIcon('Moon');
    const IconLogOut = resolveIcon('LogOut');

    return (
        <div ref={navRef}>
            {/* Mobile Top Header (always visible on mobile) */}
            <div className="mobile-header">
                <button 
                    className="mobile-hamburger"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    aria-label="Toggle navigation"
                >
                    {mobileMenuOpen ? <IconX size={24} /> : <IconMenu size={24} />}
                </button>
                <div className="mobile-logo">
                    <svg className="hexagon-logo" viewBox="0 0 100 100" width="24" height="24">
                        <polygon points="50,5 95,25 95,75 50,95 5,75 5,25" fill="none" stroke="#00f0ff" strokeWidth="6" />
                        <polygon points="50,20 80,35 80,65 50,80 20,65 20,35" fill="none" stroke="#d946ef" strokeWidth="4" />
                    </svg>
                    <span>SALERNO City</span>
                </div>
                <div className="mobile-avatar">
                    <img src={userData?.avatarUrl || 'https://cdn.discordapp.com/embed/avatars/0.png'} alt="Avatar" />
                </div>
            </div>

            {/* Backdrop for mobile navigation drawer */}
            {mobileMenuOpen && <div className="sidebar-backdrop" onClick={() => setMobileMenuOpen(false)} />}

            {/* Sidebar Element */}
            <aside className={`sidebar-container ${mobileMenuOpen ? 'mobile-open' : ''}`}>
                {/* Brand Header */}
                <div className="sidebar-brand" onClick={() => handleItemClick('dashboard')}>
                    <svg className="hexagon-logo" viewBox="0 0 100 100" width="32" height="32">
                        <polygon points="50,5 95,25 95,75 50,95 5,75 5,25" fill="none" stroke="#00f0ff" strokeWidth="6" />
                        <polygon points="50,20 80,35 80,65 50,80 20,65 20,35" fill="none" stroke="#d946ef" strokeWidth="4" />
                    </svg>
                    <span className="brand-text">SALERNO City</span>
                </div>

                {/* Sidebar Navigation Links */}
                <nav className="sidebar-nav">
                    {navigationStructure.map(category => {
                        if (!canShowCategory(category)) return null;

                        const visibleItems = category.items.filter(item => canShowItem(item));
                        if (visibleItems.length === 0) return null;

                        const isExpanded = !!expandedCategories[category.id];
                        const hasMultiple = visibleItems.length > 1;

                        if (hasMultiple) {
                            return (
                                <div key={category.id} className="nav-group-wrapper">
                                    <button 
                                        className={`nav-group-header ${isExpanded ? 'expanded' : ''}`}
                                        onClick={() => toggleCategory(category.id)}
                                        style={{ '--item-accent': category.color }}
                                    >
                                        <div className="group-title-content">
                                            {getIcon(category.id, category.icon)}
                                            <span className="group-name">{category.name}</span>
                                        </div>
                                        <IconChevronDown size={14} className="group-arrow" />
                                    </button>
                                    <div className={`nav-group-items ${isExpanded ? 'show' : ''}`}>
                                        {visibleItems.map(item => (
                                            <button
                                                key={item.id}
                                                className={`nav-item ${currentView === item.id ? 'active' : ''}`}
                                                onClick={() => handleItemClick(item.id)}
                                            >
                                                {getIcon(item.id, item.icon)}
                                                <span className="item-name">{item.name}</span>
                                                {currentView === item.id && <div className="active-glow-indicator" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        } else {
                            // Single item categories directly as links
                            const singleItem = visibleItems[0];
                            return (
                                <button
                                    key={singleItem.id}
                                    className={`nav-item single ${currentView === singleItem.id ? 'active' : ''}`}
                                    onClick={() => handleItemClick(singleItem.id)}
                                    style={{ '--item-accent': category.color }}
                                >
                                    {getIcon(singleItem.id, singleItem.icon)}
                                    <span className="item-name">{singleItem.name}</span>
                                    {currentView === singleItem.id && <div className="active-glow-indicator" />}
                                </button>
                            );
                        }
                    })}
                </nav>

                {/* System Status Sidebar Widget */}
                <div className="sidebar-status-container">
                    <div className="status-title">SYSTEM STATUS</div>
                    
                    <div className="sidebar-status-item">
                        <div className="status-labels">
                            <span>Core Systems</span>
                            <span className="status-percentage cyan">89%</span>
                        </div>
                        <div className="status-progressbar">
                            <div className="status-progress-fill bg-cyan" style={{ width: '89%' }} />
                        </div>
                    </div>

                    <div className="sidebar-status-item">
                        <div className="status-labels">
                            <span>Security</span>
                            <span className="status-percentage green">75%</span>
                        </div>
                        <div className="status-progressbar">
                            <div className="status-progress-fill bg-green" style={{ width: '75%' }} />
                        </div>
                    </div>

                    <div className="sidebar-status-item">
                        <div className="status-labels">
                            <span>Network</span>
                            <span className="status-percentage purple">89%</span>
                        </div>
                        <div className="status-progressbar">
                            <div className="status-progress-fill bg-purple-blue" style={{ width: '89%' }} />
                        </div>
                    </div>
                </div>
            </aside>

            {/* Top Header Panel (desktop utility header) */}
            <header className="top-header-panel">
                {/* Search systems */}
                <div className="header-search">
                    <IconSearch className="search-icon" size={16} />
                    <input type="text" placeholder="Search systems..." />
                </div>

                {/* Right side utilities */}
                <div className="header-actions">
                    <button className="utility-btn notification-bell" aria-label="Notifications">
                        <IconBell size={18} />
                        <span className="notification-dot" />
                    </button>
                    <button className="utility-btn theme-toggle" aria-label="Toggle theme">
                        <IconMoon size={18} />
                    </button>
                    
                    <div className="header-divider" />

                    {/* User profile details */}
                    <div className="user-profile-badge">
                        <div className="avatar-wrapper">
                            <img src={userData?.avatarUrl || 'https://cdn.discordapp.com/embed/avatars/0.png'} alt="Avatar" className="user-avatar-img" />
                            <span className="online-badge-dot" />
                        </div>
                        <div className="user-meta-details">
                            <span className="user-display-name">{userData?.nickname || userData?.global_name || 'Benutzer'}</span>
                            <span className={`user-role-label ${isGlobalAdmin ? 'admin' : 'member'}`}>
                                {isGlobalAdmin ? 'Super Admin' : (userData?.assignedRoles?.[0]?.name || 'Mitglied')}
                            </span>
                        </div>
                    </div>

                    {/* Elegant logout button */}
                    <button className="header-logout-btn" onClick={handleLogout} title="Abmelden">
                        <IconLogOut size={16} />
                        <span>Abmelden</span>
                    </button>
                </div>
            </header>
        </div>
    );
};

export default MainNavigation;
